import {Component, OnInit, ViewChild} from '@angular/core';
import {TableColumnsConfigurationDialogComponent} from '../../dialogs/table-columns-configuration-dialog/table-columns-configuration-dialog.component';
import {DisplayedColumnInf} from '../../interfaces/common/table/displayed-column-inf';
import {MatDialog} from '@angular/material/dialog';
import {UserPrivilegeService} from '../../services/common/user-privilege/user-privilege.service';
import {UsersApiService} from '../../services/api/users/users/users-api.service';
import {ConfirmationDialogComponent} from '../../dialogs/confirmation-dialog/confirmation-dialog.component';
import {InformationDialogComponent} from '../../dialogs/information-dialog/information-dialog.component';
import {UsersTabSearchComponent} from './users-tab-search/users-tab-search.component';
import {PageEventCursorInf} from '../../classes/common/table/page-event-cursor';
import * as _ from 'lodash';
import {UsersTabDialogComponent} from './users-tab-dialog/users-tab-dialog.component';
import {LogicalConjunctionService} from '../../services/common/logical-conjunction/logical-conjunction.service';
import {SnackBarService} from '../../services/common/snack-bar/snack-bar.service';
import {AttributesApiService} from '../../services/api/users/attributes/attributes-api.service';
import {GroupsApiService} from '../../services/api/users/groups/groups-api.service';
import {ArmTableComponent} from '../../components/arm-table/arm-table.component';
import {LoaderService} from '../../services/common/loader-service/loader.service';
import {SearchTermDefinitionInf} from '../../interfaces/common/search/search-term-definition-inf';
import {AcDialogService} from 'ac-infra';
import {UsersExportDialogComponent} from './users-export-dialog/users-export-dialog.component';
import {RoleTypeEnum} from '../../services/enums/role-type.enum';
import {Sort} from '@angular/material/sort';

@Component({
  selector: 'users-tab',
  templateUrl: './users-tab.component.html',
  styleUrls: ['./users-tab.component.scss']
})
export class UsersTabComponent implements OnInit {

  @ViewChild(ArmTableComponent) armTable;

  selection: any = [];
  isUnauthorized: boolean;
  data;
  userGroupIdToName;
  search: any = {};
  sort = null;

  displayedColumns: Array<DisplayedColumnInf> = [];

  searchData = {};
  searchTerms: SearchTermDefinitionInf[] = [
    { term: 'name', operator: ':', displayName: 'Name' },
    { term: 'origin', operator: '=', displayName: 'Origin', displayValue: (value) => `${value.type} - ${value.text}`}
  ];

  pageEventCursor: PageEventCursorInf;
  usersSearchComponent = UsersTabSearchComponent;

  constructor(public dialog: MatDialog,
              private usersApiService: UsersApiService,
              private attributesApiService: AttributesApiService,
              private userPrivilege: UserPrivilegeService,
              private snackBarService: SnackBarService,
              private groupsApiService: GroupsApiService,
              private logicalConjunctionService: LogicalConjunctionService,
              private loaderService: LoaderService,
              private acDialogService: AcDialogService) { }

  ngOnInit(): void {
    this.isUnauthorized = !this.userPrivilege.isAuthorized(RoleTypeEnum.USER_MANAGEMENT);

    this.pageEventCursor = {
      before: null,
      after: null,
      pageSize: 25
    };

    this.refresh();
  }

  addItem(): void {
    this.usersApiService.prepareDataForNewUser().then(newUserTemplate => {
      const dialogRef = this.dialog.open(UsersTabDialogComponent, {
        width: '500px',
        data: {
          isEditMode: false,
          selectedItem: newUserTemplate
        }
      });

      dialogRef.componentInstance.onApply.subscribe(result => {
        this.usersApiService.addUser(result).then(() => {
          dialogRef.close();
          this.snackBarService.display(`"${result.name}" successfully added`);
          this.refreshItems(true);
        });
      });
    });
  }

  editItem(): void {
    const selectedItem = _.cloneDeep(this.selection[0]);
    selectedItem.groupsDisplayName = [];
    selectedItem.groups.map(groupId => {
      selectedItem.groupsDisplayName.push(this.userGroupIdToName[groupId]);
    });

    const dialogRef = this.dialog.open(UsersTabDialogComponent, {
      width: '500px',
      data: {
        isEditMode: true,
        selectedItem
      }
    });

    dialogRef.componentInstance.onApply.subscribe(result => {
      this.usersApiService.editUser(result).then(() => {
        dialogRef.close();
        this.snackBarService.display(`"${result.name}" successfully updated`);
        this.refreshItems(true);
      });
    });
  }

  removeItems(): void {
    const ids = this.selection.map(connection => connection.id);

    const confirmationMessage = `Are you sure you want to delete the selected ${ids.length === 1 ?
        `user: "${this.selection[0].name || 'N\\A'}"` :
        `${ids.length} users: ${this.logicalConjunctionService
            .generateAndDescription(this.selection, 'name')}`} ?`;

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        message: confirmationMessage
      }
    });

    dialogRef.componentInstance.onApply.subscribe(() => {
      this.usersApiService.deleteUsers(this.selection.map(selectionItem => selectionItem.id)).then(() => {
        dialogRef.close();
        this.refreshItems(true);
        this.snackBarService.display(`${this.logicalConjunctionService
            .generateAndDescription(this.selection, 'name')} successfully deleted`);
      });
    });
  }

  refresh(): void {
    const params = {
      limit: this.pageEventCursor.pageSize,
      cursor: _.isNil(this.pageEventCursor.after) ? null : this.pageEventCursor
    };
    this.loaderService.showMainContainer();
    Promise.all([
      this.attributesApiService.getUserDisplayAttributes(),
      this.usersApiService.getUsers(params),
      this.groupsApiService.getGroups(),
      this.usersApiService.generateOriginOptions()
    ]).then((data) => {
      this.displayedColumns = data[0];
      this.data = data[1].users;
      this.pageEventCursor.before = data[1].cursor.before;
      this.pageEventCursor.after = data[1].cursor.after;
      this.userGroupIdToName = _.mapValues(_.keyBy(data[2], 'id'), 'name');
      this.setSearchOptions(data[3]);
      this.loaderService.hideMainContainer();
    }).catch(err => {
      console.log(`Exception was thrown in refresh ${err}`);
      this.loaderService.hideMainContainer();
    });
  }

  refreshItems(keepPage?: boolean): void {
    if (keepPage) {
      this.pageEventCursor.direction = this.pageEventCursor.currentPageDirection;
      this.pageEventCursor.after = this.pageEventCursor.currentPageAfter;
      this.pageEventCursor.before = this.pageEventCursor.currentPageBefore;
    }
    let params = {
      limit: this.pageEventCursor.pageSize,
      cursor: _.isNil(this.pageEventCursor.after) ? null : this.pageEventCursor,
      sort: _.isNil(this.sort) ? null : this.sort
    };

    params = {...this.search, ...params};
    this.loaderService.showMainContainer();
    this.usersApiService.getUsers(params).then((data) => {
      this.data = data.users;
      this.pageEventCursor.before = data.cursor.before;
      this.pageEventCursor.after = data.cursor.after;
      this.loaderService.hideMainContainer();
    }).catch((error) => {
      console.log(`Exception was thrown in refresh users, reason: ${error}`);
      this.loaderService.hideMainContainer();
    });
  }

  isSingleSelection(): boolean {
    return this.selection && this.selection.length === 1;
  }

  isAnySelection(): boolean {
    return  this.selection && this.selection.length > 0;
  }

  openColumnsConfiguration(): void {
    const dialogRef = this.dialog.open(TableColumnsConfigurationDialogComponent, {
      width: '450px',
      data: {
        columns: this.displayedColumns
      }
    });

    dialogRef.componentInstance.onApply.subscribe(() => {
      dialogRef.close();
    });
  }

  getNumberOfUsers(): void {
    this.usersApiService.getUsersCount().then(totalUsers => {
      const dialogRef = this.dialog.open(InformationDialogComponent, {
        width: '460px',
        data: {
          message: `Total amount of users is ${totalUsers}`
        }
      });

      dialogRef.componentInstance.onApply.subscribe(() => {
        dialogRef.close();
      });
    });
  }

  exportToCSV(): void {
    const parameters = this.search;

    const dialogData = {enclose : false};
    this.acDialogService.open(UsersExportDialogComponent, {
      dialogData,
      title: 'Export users',
      onSubmit: () => {
        parameters.enclose = dialogData.enclose;
        this.usersApiService.exportUsersToCsv(parameters);
      }
    });
  }

  onSearch(data): void {
    this.armTable.firstPage();
    this.search = data;

    this.refreshItems();
  }

  onSelection(selection: any): void {
    this.selection = selection;
  }

  pageChanged(event: PageEventCursorInf): void {
    this.pageEventCursor = event;
    this.refreshItems();
  }

  private setSearchOptions(originOptions): void {
    this.searchData = {
      originOptions
    };
  }

  sortChange(event: Sort): void {
    if (event.direction === 'desc'){
      this.sort = '-' + event.active;
    } else {
      this.sort = event.active;
    }
    this.refreshItems(false);
  }
}
